import { Flo } from '../../types/flo-live-api'
import { getAuthToken } from '../../utils/authentication'
import { getText } from '../../utils/dictionary'
import { floFetch, getLiveApiUrL } from '../../utils/network'
import { shouldDeviceHaveNoDVR } from '../../utils/info'

export class FloLive {
  protected BASE_URL = getLiveApiUrL()

  public async getFreeStream(
    reqParams: Flo.Live.FreeStreamRequest,
  ): Promise<Flo.Live.StreamData | ErrorType> {
    try {
      const fields = reqParams?.fields ? `?fields=${encodeURIComponent(reqParams.fields)}` : ''
      const res = await floFetch(`${this.BASE_URL}/streams/${reqParams?.streamId}${fields}`, {
        method: 'POST',
      })

      if ('error' in res) return res

      const streamResp = (await res.json()) as Flo.Live.FreeStreamResponse
      return streamResp.data
    } catch (error) {
      console.error(error)
    }
  }

  public async getPremiumStream(
    reqParams: Flo.Live.PremiumStreamRequest,
  ): Promise<Flo.Live.PremiumStreamResponse | ErrorType> {
    try {
      const dvrEnabled = shouldDeviceHaveNoDVR() ? false : undefined
      console.log(`[getPremiumStream] dvrEnabled: ${dvrEnabled}`)
      const res = await floFetch(`${this.BASE_URL}/streams/${reqParams?.streamId}/tokens`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adTracking: reqParams.mediaTailor,
          ...(dvrEnabled !== undefined && { dvrEnabled }),
        }),
      })

      if ('error' in res) return res

      const streamResp = await res.json()
      return streamResp.data
    } catch (error) {
      console.error(error)
    }
  }

  public async getAdUrls(
    reqParams: Flo.Live.AdUrlsRequest,
  ): Promise<Flo.Live.AdUrlsResponse | ErrorType> {
    try {
      const adsInfo = this.getAdsSessionUrl(reqParams?.manifestUrl)
      const res = await floFetch(adsInfo[0], {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: adsInfo[1],
      })

      if ('error' in res) return res

      const streamResp = await res.json()
      return streamResp
    } catch (error) {
      console.error(error)
    }
  }

  private getAdsSessionUrl(manifestUrl: string): [string, string] {
    let newManifestUrl = manifestUrl.replace('/v1/master/', '/v1/session/').split('?')[0] + '?'
    let allQueryParams = manifestUrl.split('?')[1].split('&')
    let body = {}
    let adsQueryParams = {}

    allQueryParams.forEach((queryParam) => {
      if (!queryParam.startsWith('ads.'))
        newManifestUrl = newManifestUrl.concat(queryParam).concat('&')
      else {
        let queryParamName = queryParam.split('=')[0].replace('ads.', '')
        adsQueryParams[queryParamName] = queryParam.split('=')[1]
      }
    })

    body['adsParams'] = adsQueryParams

    return [newManifestUrl, JSON.stringify(body)]
  }

  public async getPremiumStreamPreviewToken(
    reqParams: Flo.Live.PremiumStreamRequest,
  ): Promise<Flo.Live.PremiumStreamResponse | ErrorType> {
    try {
      const res = await floFetch(`${this.BASE_URL}/streams/${reqParams?.streamId}/tokens/preview`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if ('error' in res) return res

      const streamResp = await res.json()
      return streamResp.data
    } catch (error) {
      console.error(error)
    }
  }

  public async checkGeoRestriction(reqParams: Flo.Live.GeoCheckRequest): Promise<boolean> {
    try {
      const res = await floFetch(`${this.BASE_URL}/events/${reqParams.liveId}/geo`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if ('error' in res) return false

      const geoResp = await res.json()

      if (geoResp.error) {
        return false
      }

      return geoResp.data
    } catch (error) {
      console.error(error)
    }
  }

  public async sendLiveHeartbeat(reqParams: Flo.Live.LiveHeartbeatRequest): Promise<boolean> {
    let errMsg = getText('playerGenericHeartbeatError')
    try {
      const res = await floFetch(`${this.BASE_URL}/tokens/${reqParams?.tokenId}/heartbeat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })

      if ('error' in res) return false

      if (res.status >= 200 && res.status < 300) {
        return true
      }
      const data = await res.json()
      errMsg ??= data?.error?.message
    } catch (error) {
      console.error(error)
    }

    throw new Error(errMsg)
  }

  public async sendAdsHeartbeat(reqParams: Flo.Live.AdsHeartbeatRequest) {
    try {
      await floFetch(reqParams.trackingUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      })
    } catch (error) {
      console.error(error)
    }
  }
}
